import { useEffect, useRef, useState } from "react"
import { Play } from "lucide-react"
import { cn } from "@/lib/utils"

type Props = {
  stream: MediaStream | null
  muted?: boolean
  mirror?: boolean
  className?: string
  label?: string
}

// A <video> bound to a MediaStream. srcObject can't be set declaratively,
// so it's attached in an effect; the element is otherwise inert during SSR.
// It fills its nearest positioned ancestor, so wrap it in a `relative` box.
// If the browser refuses to autoplay (audio without a user gesture), a
// play button is offered instead of a silent black rectangle.
export function VideoView({ stream, muted = false, mirror = false, className, label }: Props) {
  const ref = useRef<HTMLVideoElement>(null)
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.srcObject = stream
    setBlocked(false)
    if (stream) {
      el.play().catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "NotAllowedError") setBlocked(true)
      })
    }
    return () => {
      el.srcObject = null
    }
  }, [stream])

  function resume() {
    ref.current
      ?.play()
      .then(() => setBlocked(false))
      .catch(() => {})
  }

  return (
    <>
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        aria-label={label}
        className={cn("absolute inset-0 h-full w-full bg-black object-cover", mirror && "-scale-x-100", className)}
      />
      {blocked && (
        <button
          type="button"
          onClick={resume}
          className="btn btn-primary absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <Play className="h-4 w-4" />
          <span className="ml-2">Click to play</span>
        </button>
      )}
    </>
  )
}
